import { z } from 'zod';
import { paymentOrderStatuses, type JusticeDePaix, type PaymentOrderStatus } from '../enums.js';
import { paginationFields } from './_pagination.js';

/**
 * Contrat réseau de la requête en ordonnance de paiement luxembourgeoise.
 *
 * Les deux parties sont figées en instantané au dépôt : une requête déposée au greffe ne se
 * réécrit pas parce que le patient a déménagé. La Justice de Paix est déduite de la commune du
 * patient côté serveur ; la V1 la laissait choisir dans le formulaire et la déduisait ailleurs
 * d'un préfixe de code postal, deux règles pour une seule compétence.
 */

export const paymentOrderIdParamSchema = z.object({ id: z.uuid() }).strict();

/** Le dépôt ne prend aucun paramètre : tout est déduit de la facture, du cabinet et du patient. */
export const paymentOrderCreateSchema = z.object({}).strict();

/** Transitions autorisées : `draft → filed → granted → paid`, `cancelled` depuis draft ou filed. */
export const paymentOrderStatusSchema = z.object({ status: z.enum(paymentOrderStatuses) }).strict();
export type PaymentOrderStatusInput = z.infer<typeof paymentOrderStatusSchema>;

export const paymentOrderListQuerySchema = z
  .object({
    status: z.enum(paymentOrderStatuses).optional(),
    invoiceId: z.uuid().optional(),
    /** La consultation de la **facture** poursuivie : une ordonnance n'en porte pas elle-même. */
    encounterId: z.uuid().optional(),
    ...paginationFields,
  })
  .strict();
export type PaymentOrderListQuery = z.infer<typeof paymentOrderListQuerySchema>;

/** Identité d'une partie, recopiée au dépôt. Aucune donnée de santé n'y figure. */
export interface PartySnapshot {
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  /**
   * Profession de la partie, exigée par l'art. 131 NCPC. Celle du débiteur vient du dossier ; nulle,
   * la requête laisse la ligne à compléter. Le créancier est ostéopathe : la requête l'imprime.
   */
  profession?: string | null;
  /** Numéro d'autorisation d'exercer du créancier, quand il en a un. */
  authorizationNumber?: string | null;
  rcsNumber?: string | null;
  vatNumber?: string | null;
}

export interface PaymentOrderDto {
  id: string;
  invoiceId: string;
  status: PaymentOrderStatus;
  justiceDePaix: JusticeDePaix;
  creditorSnapshot: PartySnapshot;
  debtorSnapshot: PartySnapshot;
  /**
   * Reste dû de la facture au jour de la préparation, augmenté des intérêts de retard encore dus :
   * ceux de la dernière relance envoyée, seuls. Figé comme la requête.
   */
  totalCents: number;
  filedAt: string | null;
  grantedAt: string | null;
  expiresAt: string | null;
  paidAt: string | null;
  documentId: string | null;
  createdAt: string;
  updatedAt: string;
}
